import React, { useEffect, useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AutocompleteDropdown from '../../../components/ui/AutocompleteDropdown';
import { Checkbox } from '../../../components/ui/Checkbox';
import { promptFieldService } from '../../../services/promptFieldService';

const DynamicInputForm = ({ promptFields, formData, onChange }) => {
  const [fieldOptionsMap, setFieldOptionsMap] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Load options for all fields when promptFields change
  useEffect(() => {
    const loadAllFieldOptions = async () => {
      if (!promptFields || promptFields?.length === 0) return;
      
      setLoadingOptions(true);
      const optionsMap = {};
      
      try {
        await Promise.all(
          promptFields?.map(async (field) => {
            const options = await promptFieldService?.getPromptFieldOptions(field?.id);
            if (options?.length > 0) {
              optionsMap[field?.id] = options?.map(opt => ({
                value: opt?.optionValue,
                label: opt?.optionLabel
              }));
            }
          })
        );
        setFieldOptionsMap(optionsMap);
      } catch (err) {
        console.error('Error loading field options:', err);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadAllFieldOptions();
  }, [promptFields]);

  const handleInputChange = (fieldName, value) => {
    onChange({ ...formData, [fieldName]: value });
  };

  if (!promptFields || promptFields?.length === 0) {
    return (
      <div className="flex items-center justify-center p-12 bg-muted rounded-lg">
        <p className="text-muted-foreground text-center">
          Please select a use case and AI tool to see available fields
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {promptFields?.map((field) => {
        const fieldValue = formData?.[field?.fieldName] || '';
        const hasOptions = fieldOptionsMap?.[field?.id]?.length > 0;
        const normalizedFieldType = field?.fieldType?.toLowerCase();

        if (normalizedFieldType === 'textarea') {
          return (
            <div key={field?.id}>
              <label className="block text-sm font-medium text-foreground mb-1">
                {field?.fieldLabel}
                {field?.isRequired && <span className="text-destructive ml-1">*</span>}
              </label>
              {field?.description && (
                <p className="text-xs text-muted-foreground mb-2">{field?.description}</p>
              )}
              <textarea
                value={fieldValue}
                onChange={(e) => handleInputChange(field?.fieldName, e?.target?.value)}
                placeholder={field?.placeholder}
                required={field?.isRequired}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          );
        }

        if (normalizedFieldType === 'checkbox') {
          return (
            <Checkbox
              key={field?.id}
              label={field?.fieldLabel}
              checked={fieldValue === true || fieldValue === 'true'}
              onChange={(e) => handleInputChange(field?.fieldName, e?.target?.checked)}
              description={field?.description}
            />
          );
        }

        if (normalizedFieldType === 'select') {
          // Use AutocompleteDropdown if field has predefined options, otherwise use regular Select
          if (hasOptions) {
            return (
              <AutocompleteDropdown
                key={field?.id}
                label={field?.fieldLabel}
                description={field?.description}
                options={fieldOptionsMap?.[field?.id]}
                value={fieldValue}
                onChange={(value) => handleInputChange(field?.fieldName, value)}
                required={field?.isRequired}
                placeholder={field?.placeholder || `Select or type ${field?.fieldLabel?.toLowerCase()}...`}
                loading={loadingOptions}
              />
            );
          } else {
            // Fallback to comma-separated options from placeholder
            const options = field?.placeholder?.split(',')?.map(opt => ({
              value: opt?.trim(),
              label: opt?.trim()
            })) || [];

            return (
              <Select
                key={field?.id}
                label={field?.fieldLabel}
                description={field?.description}
                options={options}
                value={fieldValue}
                onChange={(value) => handleInputChange(field?.fieldName, value)}
                required={field?.isRequired}
                placeholder={`Select ${field?.fieldLabel?.toLowerCase()}...`}
              />
            );
          }
        }

        return (
          <Input
            key={field?.id}
            label={field?.fieldLabel}
            type="text"
            placeholder={field?.placeholder}
            value={fieldValue}
            onChange={(e) => handleInputChange(field?.fieldName, e?.target?.value)}
            required={field?.isRequired}
            description={field?.description}
          />
        );
      })}
    </div>
  );
};

export default DynamicInputForm;